import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Download, Printer, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, frDate } from "@/lib/api";

const frDateTime = (iso) =>
  (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

async function fetchPdf(url) {
  const { data } = await api.get(url, { responseType: "blob" });
  return URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
}

const PdfButtons = ({ url, filename, testid }) => {
  const [busy, setBusy] = useState(false);
  const run = async (mode) => {
    setBusy(true);
    try {
      const href = await fetchPdf(url);
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = href; a.download = filename; a.click();
      } else {
        // "noopener" makes window.open() return null even on success: open first, then cut the opener.
        const w = window.open(href, "_blank");
        if (!w) toast.error("Autorisez les fenêtres pop-up pour afficher le PDF");
        else {
          if (mode === "print") w.addEventListener("load", () => w.print());
          w.opener = null;
        }
      }
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "PDF indisponible");
    } finally { setBusy(false); }
  };
  const cls = "inline-flex items-center gap-1.5 border border-white/20 px-2.5 py-1.5 text-[11px] uppercase text-bone";
  return (
    <div className="flex flex-wrap gap-2">
      <button onClick={() => run("view")} disabled={busy} className={cls} data-testid={`${testid}-view`}>
        {busy ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Afficher
      </button>
      <button onClick={() => run("download")} disabled={busy} className={cls} data-testid={`${testid}-download`}>
        <Download size={13} /> Télécharger
      </button>
      <button onClick={() => run("print")} disabled={busy} className={cls} data-testid={`${testid}-print`}>
        <Printer size={13} /> Imprimer
      </button>
    </div>
  );
};

export default function BookingDocuments({ booking, onClose }) {
  const panel = useRef(null);
  const [police, setPolice] = useState(null);
  useEffect(() => { panel.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [booking.id]);
  const load = useCallback(() => api.get(`/admin/bookings/${booking.id}/police-status`)
    .then((r) => setPolice(r.data))
    .catch((e) => toast.error(apiError(e.response?.data?.detail))), [booking.id]);
  useEffect(() => { load(); }, [load]);

  const pending = (police?.travellers || []).filter((t) => !t.signed).length;

  return (
    <div ref={panel} className="mt-5 border border-brass/40 bg-panel p-6" data-testid="documents-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-bone">Documents — {booking.name}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {frDate(booking.start_date)} → {frDate(booking.end_date)} · accès réservé aux
            propriétaires, aucun lien public, documents non indexables.
          </p>
        </div>
        <button onClick={onClose} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="documents-close">Fermer</button>
      </div>

      <div className="mt-5 border-t border-white/10 pt-4">
        <h4 className="text-sm font-semibold text-bone">Contrat de location</h4>
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="contract-accepted-at">
          {(police?.cgv_accepted_at || booking.cgv_accepted_at)
            ? `Conditions générales et politique d'annulation acceptées le ${frDateTime(police?.cgv_accepted_at || booking.cgv_accepted_at)} (heure UTC)`
            : "Aucune acceptation en ligne enregistrée (réservation saisie manuellement ou antérieure au contrat PDF)"}
        </p>
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="contract-archive">
          {police?.contract_archived_at
            ? `Version figée archivée le ${frDateTime(police.contract_archived_at)} · conservée jusqu'au ${frDateTime(police.contract_retention_until)} (10 ans) · empreinte ${String(police.contract_sha256).slice(0, 12)}…`
            : "Pas encore d'archive figée : le PDF est régénéré à partir des données de la réservation"}
        </p>
        <div className="mt-3">
          <PdfButtons url={`/admin/bookings/${booking.id}/contract`} testid="contract-pdf"
            filename={`Contrat-${booking.name.replace(/\s+/g, "-")}-${booking.start_date}.pdf`} />
        </div>
      </div>

      <div className="mt-5 border-t border-white/10 pt-4">
        <h4 className="text-sm font-semibold text-bone">Fiches individuelles de police</h4>
        {!police && <p className="mt-2 text-xs text-muted-foreground">Chargement…</p>}
        {police && !police.travellers.length && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="police-none">
            Aucun voyageur de nationalité étrangère : aucune fiche n'est requise pour ce séjour.
          </p>
        )}
        {police && !!police.travellers.length && (
          <>
            <p className={`mt-1 text-[11px] ${pending ? "text-destructive" : "text-brass"}`} data-testid="police-summary">
              {pending
                ? `${pending} fiche(s) à signer sur ${police.travellers.length}`
                : `Toutes les fiches sont signées (${police.travellers.length})`}
            </p>
            {!!(police.reminders || []).length && (
              <div className="mt-2 text-[11px] text-muted-foreground" data-testid="police-reminders">
                Rappels envoyés :{" "}
                {police.reminders.map((r) => `${r.stage === "48h" ? "48 h" : "24 h"} avant l'arrivée le ${frDateTime(r.sent_at)}`).join(" · ")}
              </div>
            )}
            {!(police.reminders || []).length && (
              <p className="mt-2 text-[11px] text-muted-foreground" data-testid="police-reminders-none">
                Aucun rappel envoyé pour l'instant (envoi automatique 48 h puis 24 h avant l'arrivée,
                uniquement si la fiche n'est pas signée)
              </p>
            )}
            {police.form_url && (
              <div className="mt-2 flex min-w-0 items-center gap-2 bg-white/5 p-2 text-[11px]">
                <span className="min-w-0 flex-1 truncate" data-testid="police-form-url">{police.form_url}</span>
                <button onClick={() => { navigator.clipboard.writeText(police.form_url); toast.success("Lien copié"); }}
                  className="shrink-0 text-bone" data-testid="copy-police-url"><Copy size={13} /></button>
              </div>
            )}
            <div className="mt-3 space-y-3">
              {police.travellers.map((t) => (
                <div key={t.index} className="bg-white/5 p-3" data-testid={`police-traveller-${t.index}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="text-sm text-bone">
                      {t.last_name} {t.first_name}
                      <span className="ml-2 text-[11px] text-muted-foreground">{t.nationality}</span>
                    </p>
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase ${t.signed ? "bg-brass text-ink" : t.prepared ? "bg-white/15 text-bone" : "bg-brass/20 text-brass"}`}
                      data-testid={`police-state-${t.index}`}>
                      {t.signed ? "signée" : t.prepared ? "préparée" : "à signer"}
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t.signed
                      ? `Signée le ${frDateTime(t.signed_at)} (UTC) · suppression automatique le ${frDateTime(t.expires_at)}`
                      : t.prepared
                        ? `Informations préparées par le voyageur le ${frDateTime(t.prepared_at)} — signature à recueillir à l'arrivée`
                        : "En attente de la signature du voyageur (à l'arrivée)"}
                  </p>
                  {t.signed && (
                    <div className="mt-2">
                      <PdfButtons url={`/admin/bookings/${booking.id}/police/${t.index}/pdf`}
                        testid={`police-pdf-${t.index}`}
                        filename={`Fiche-police-${(t.last_name || "voyageur").replace(/\s+/g, "-")}-${booking.start_date}.pdf`} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
